// LOGIN URL UTIL

const productionBaseDomains = [
	'engros.ae',
	'engros.ai',
	'engros.app',
	'engros.at',
	'engros.dev',
	'engros.es',
	'engros.fi',
	'engros.in',
	'engros.io',
	'engros.it',
	'engros.nz',
	'engros.pt',
	'engros.se',
	'engros.uk',
	'partihandel.io',
	'partihandel.se',
	'partihanteraren.com',
	'pifa.se',
	'lotusfoodservice.se',
	'swecofoodservice.se',
	'swecofoodservice.com',
];

const getBaseDomain = host => {
	const matchedDomain = productionBaseDomains.find(domain => host.endsWith(domain));
	return matchedDomain || null;
};

const getSubdomainPrefix = (host, baseDomain) => {
	if (!baseDomain) return '';

	if (host === baseDomain) return '';

	const prefix = host.replace(`.${baseDomain}`, '');

	const parts = prefix.split('.');

	// If first part is a service subdomain (id, app, api), strip it
	if (parts.length > 0 && ['id', 'app', 'api'].includes(parts[0])) {
		return parts.slice(1).join('.');
	}

	return prefix;
};

// Base domains that are ours-but-not-production. They stay in
// productionBaseDomains above so getSubdomainPrefix keeps parsing their
// subdomains, but they must report `sandbox`. Mirrors SANDBOX_BASE_DOMAINS in
// application/src/utils/UrlUtil.js and SANDBOX_DOMAINS in
// pifa-api/src/config/CorsConfig.js.
const SANDBOX_BASE_DOMAINS = ['engros.dev'];

const getEnvironmentAndDomain = () => {
	const host = window.location.host;

	if (host.includes('pifa.dev')) {
		return {environment: 'sandbox', domain: 'pifa.dev', subdomain: ''};
	}

	if (host.includes('id.localhost:8080') || host.includes('localhost:8080')) {
		return {
			environment: 'localproduction',
			domain: 'localhost:8080',
			subdomain: '',
		};
	}

	if (host.includes('localhost')) {
		return {environment: 'development', domain: 'localhost', subdomain: ''};
	}

	const baseDomain = getBaseDomain(host);

	if (baseDomain) {
		const subdomain = getSubdomainPrefix(host, baseDomain);
		return {environment: SANDBOX_BASE_DOMAINS.includes(baseDomain) ? 'sandbox' : 'production', domain: baseDomain, subdomain};
	}

	return {environment: 'development', domain: 'localhost', subdomain: ''};
};

const {environment, domain, subdomain} = getEnvironmentAndDomain();

// Local dev ports are overridable so several dev stacks can run side by side on
// one machine (dev/stack.sh <slot>). CRA bakes REACT_APP_* in when the dev
// server starts; unset, they fall back to the classic 3000/8000 ports.
const DEV_API_URL = process.env.REACT_APP_DEV_API_URL || 'http://localhost:8000';
const DEV_APP_URL = process.env.REACT_APP_DEV_APP_URL || 'http://localhost:3000';

// Customer-controlled domains - mirrors pifa-loadbalancer/server.js's
// `customerDomains` list. Used to hide Pifa-branded chrome (e.g. the
// partihandel.se social media icons) that shouldn't appear on a tenant's
// own domain. Keep in sync with server.js if a new customer domain is added.
const customerDomains = ['lotusfoodservice.se', 'swecofoodservice.se', 'swecofoodservice.com'];

const isCustomerControlledDomain = (() => {
	const host = window.location.host.split(':')[0];
	return customerDomains.some(d => host === d || host.endsWith(`.${d}`));
})();

const getApiBaseUrl = () => {
	const subdomainPart = subdomain ? `${subdomain}.` : '';

	switch (environment) {
		case 'production':
			return `https://api.${subdomainPart}${domain}`;
		case 'sandbox':
			return `https://api.${subdomainPart}${domain}`;
		default:
			return DEV_API_URL;
	}
};

const getAppUrl = () => {
	const subdomainPart = subdomain ? `${subdomain}.` : '';

	switch (environment) {
		case 'production':
			return `https://app.${subdomainPart}${domain}`;
		case 'sandbox':
			return `https://app.${subdomainPart}${domain}`;
		case 'localproduction':
			return 'http://app.localhost:8080';
		default:
			return DEV_APP_URL;
	}
};

const getStartUrl = () => {
	const subdomainPart = subdomain ? `${subdomain}.` : '';

	switch (environment) {
		case 'production':
			return `https://${subdomainPart}${domain}`;
		case 'sandbox':
			return `https://${subdomainPart}${domain}`;
		case 'localproduction':
			return 'http://localhost:8080';
		default:
			return 'http://localhost:1048';
	}
};

export {getApiBaseUrl, getAppUrl, getStartUrl, isCustomerControlledDomain};
